import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useToast } from '../context/ToastContext';
import AnnouncementBanner from '../components/ui/AnnouncementBanner';
import AlertMessage from '../components/ui/AlertMessage';
import Pagination from '../components/ui/Pagination';
import FilterTabs from '../components/ui/FilterTabs';
import { CopyIcon, TrashIcon, SpinnerIcon, WarningIcon } from '../components/ui/Icons';
import ConfirmModal from '../components/ui/ConfirmModal';
import PanelTabs from '../components/ui/PanelTabs';
import PanelCard, { EmptyState } from '../components/ui/PanelCard';
import CopyButton from '../components/ui/CopyButton';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import DashboardStats from './owner/DashboardStats';
import AuditLogTab from './owner/AuditLogTab';
import BanLogsTab from './owner/BanLogsTab';
import AnnouncementsTab from './owner/AnnouncementsTab';
import InvitesTab from './owner/InvitesTab';
import OrdersTab from './owner/OrdersTab';
import ProductsTab from './owner/ProductsTab';
import TrafficTab from './owner/TrafficTab';
import SubscriptionsTab from './owner/SubscriptionsTab';

// A kulcs állapota. A `sold` (eladva, de még nem aktiválva) SZÁNDÉKOSAN külön
// fül: az `Available` kizárja az eladottakat, tehát nélküle egy kifizetett, de
// még be nem váltott kulcs csak az `All` alatt volt megtalálható — a szerver
// pedig eddig is tudta ezt a szűrőt. Így a három szám ki is adja az összeset.
const KEY_FILTERS = [
  { id: '', label: 'All' },
  { id: 'false', label: 'Available' },
  { id: 'sold', label: 'Sold' },
  { id: 'true', label: 'Used' },
];

// Melyik darabszám tartozik az egyes állapot-fülekhez a kategória-sorban.
// (A szerver mind a négyet küldi, hogy fülváltáskor ne kelljen újrakérdezni.)
const KEY_COUNT_FIELD = { '': 'total', 'false': 'available', sold: 'sold', 'true': 'used' };

// Egy lap a kulcslistában. EGY helyen, mert korábban három helyre volt beírva
// ugyanez a 15-ös szám, és egy elfelejtett helytől némán elcsúszik a lapozás.
const KEYS_PAGE_SIZE = 15;

/**
 * Napokból olvasható időtartam.
 *
 * A 36000 napos küszöb a „Lifetime" jelölése — ugyanez a szabály él a
 * ProductPage-en, a ProductsTab-on és a nyitóoldalon is.
 */
const durationLabel = (days) =>
  days >= 36000 ? 'Lifetime' : `${days} ${days === 1 ? 'day' : 'days'}`;

const TABS = [
  { id: 'dashboard',     label: 'Dashboard',     icon: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></> },
  { id: 'products',      label: 'Products',      icon: <><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 12l9 5 9-5M3 16l9 5 9-5" /></> },
  { id: 'orders',        label: 'Orders',        icon: <><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18M16 10a4 4 0 01-8 0" /></> },
  { id: 'audit',         label: 'Audit Log',     icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></> },
  { id: 'announcements', label: 'Announcements', icon: <><path d="M3 11v2a1 1 0 001 1h2l5 4V6L6 10H4a1 1 0 00-1 1z" /><path d="M16 9a4 4 0 010 6" /></> },
  { id: 'invites',       label: 'Invites',       icon: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 7l10 6 10-6" /></> },
  { id: 'traffic',       label: 'Traffic',       icon: <><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></> },
  { id: 'subscriptions', label: 'Subscriptions', icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></> },
  { id: 'banlogs',       label: 'Ban Logs',      icon: <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></> },
];

/** Be/ki kapcsolható rendszer állapotjelzője. */
function SystemPill({ on, onLabel, offLabel, tone = 'brand' }) {
  const tones = {
    brand:   'bg-brand-500/12 border-brand-400/25 text-brand-200',
    success: 'bg-emerald-500/12 border-emerald-500/25 text-emerald-300',
    danger:  'bg-red-500/12 border-red-500/25 text-red-300',
  };
  const dots = { brand: 'bg-brand-400', success: 'bg-emerald-400', danger: 'bg-red-400' };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
      on ? tones[tone] : 'bg-white/[0.05] border-white/[0.08] text-gray-500'
    }`}>
      <span className={`w-2 h-2 rounded-full ${on ? `${dots[tone]} animate-pulse` : 'bg-gray-600'}`} />
      {on ? onLabel : offLabel}
    </div>
  );
}

export default function OwnerPanel() {
  const { toast } = useToast();
  const [ownerTab, setOwnerTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [keys, setKeys] = useState([]);
  const [keysTotal, setKeysTotal] = useState(0);
  const [keysPage, setKeysPage] = useState(1);
  const [keysFilter, setKeysFilter] = useState('');
  // Kategória = csomag (`product_plans.id`), üres sztring = mind.
  const [keysPlan, setKeysPlan] = useState('');
  const [keyCategories, setKeyCategories] = useState([]);
  // Kézi újratöltés-jelző: ha a szűrők nem változnak, ettől fut le mégis a
  // lekérdezés (pl. kulcsgenerálás után) — egyetlen kérésben, nem duplán.
  const [keysReload, setKeysReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [genForm, setGenForm] = useState({ productId: '', planId: '', quantity: 1 });
  const [genResult, setGenResult] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(null);
  const [deletingKeyId, setDeletingKeyId] = useState(null);
  const [pendingGlobalAction, setPendingGlobalAction] = useState(null);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState(null);
  const [siteStatus, setSiteStatus] = useState('working');
  const [statusLoading, setStatusLoading] = useState(false);
  const [inviteSystem, setInviteSystem] = useState('off');
  const [referralSystem, setReferralSystem] = useState('on');
  const [referralLoading, setReferralLoading] = useState(false);
  const [betaMode, setBetaMode] = useState('off');
  const [betaLoading, setBetaLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  const selectedProduct = products.find(p => p.id === parseInt(genForm.productId));

  /**
   * A kategória-fülek: egy fül csomagonként, a darabszámmal.
   *
   * A lista a KULCSOKBÓL épül (a szerver a `categories`-ban küldi), nem a
   * termék csomagjaiból: csak az kap fület, amihez tényleg van kulcs — beleértve
   * a már nyugdíjazott csomagokat is, különben a kulcsaik elérhetetlenek lennének.
   *
   * A darabszám a KIVÁLASZTOTT ÁLLAPOTHOZ tartozik (All / Available / Sold /
   * Used), mert az „van-e még szabad 30 naposom?" a tényleges kérdés. A szerver
   * mind a négy számot elküldi, tehát az állapotváltás nem jár új lekérdezéssel.
   *
   * Az azonosító a csomag ID-je, nem a napszám és nem a címke: a címke és a
   * napszám is átírható a csomagon (és nem is egyedi), az ID viszont nem
   * mozdul — kulccsal rendelkező csomagot törölni sem lehet.
   */
  const keyCountField = KEY_COUNT_FIELD[keysFilter] || 'total';
  const duplicateLabels = new Set(
    keyCategories.map(c => c.label).filter((l, i, arr) => arr.indexOf(l) !== i)
  );
  const keyCategoryFilters = [
    { id: '', label: 'All plans' },
    ...keyCategories.map(c => ({
      id: String(c.id),
      // Két csomag viselheti ugyanazt a címkét — ilyenkor a napszám különbözteti meg.
      label: `${c.label}${duplicateLabels.has(c.label) ? ` (${c.days}d)` : ''} · ${c[keyCountField]}`,
    })),
  ];

  /**
   * A kulcslista egy lapja.
   *
   * MINDEN paramétert kívülről kap, és semmilyen state-től nem függ: így egy
   * hivatkozás elég belőle a futás során, és a lenti effect nyugodtan hívhatja
   * anélkül, hogy önmagát indítaná újra.
   */
  const fetchKeys = useCallback(async (page, used, plan) => {
    const params = new URLSearchParams({ page, limit: KEYS_PAGE_SIZE });
    if (used) params.set('used', used);
    if (plan) params.set('plan', plan);
    const kr = await api.get(`/owner/keys?${params}`);
    setKeys(kr.data.keys);
    setKeysTotal(kr.data.total);
    setKeyCategories(kr.data.categories || []);
  }, []);

  // A panel többi adata CSAK induláskor jön le. Korábban a kulcs-lapozás és a
  // szűrés is újrahúzta mind az ötöt — feleslegesen, ráadásul felül is írta a
  // kapcsolók (site status, invite, beta) épp beállított állapotát.
  useEffect(() => {
    Promise.all([
      api.get('/products'),
      api.get('/owner/status'),
      api.get('/invite/status'),
      api.get('/owner/referral-system'),
      api.get('/owner/beta-mode'),
    ]).then(([pr, sr, ir, rr, br]) => {
      setProducts(pr.data.products || pr.data);
      setSiteStatus(sr.data.status);
      setInviteSystem(ir.data.inviteSystem);
      setReferralSystem(rr.data.referralSystem);
      setBetaMode(br.data.betaMode);
    }).catch(() => setMsg({ type: 'error', text: 'Failed to load data.' }))
      .finally(() => setLoading(false));
  }, []);

  // A kulcslista viszont minden lap-, állapot- és kategóriaváltásnál újratölt.
  useEffect(() => {
    fetchKeys(keysPage, keysFilter, keysPlan)
      .catch(() => setMsg({ type: 'error', text: 'Failed to load keys.' }));
  }, [fetchKeys, keysPage, keysFilter, keysPlan, keysReload]);

  // Ha a kiválasztott kategória utolsó kulcsát is töröljük, a füle eltűnik — és
  // a szűrő egy olyan csomagra mutatna, amire nincs kattintható fül. Ilyenkor
  // visszaállunk az „All plans"-re. (A `keyCategories.length` feltétel az első,
  // még üres renderre kell, különben azonnal eldobná a szűrőt.)
  useEffect(() => {
    if (keysPlan && keyCategories.length > 0
        && !keyCategories.some(c => String(c.id) === keysPlan)) {
      setKeysPlan('');
    }
  }, [keyCategories, keysPlan]);

  const generateKey = async (e) => {
    e.preventDefault();
    setGenLoading(true);
    setGenResult(null);
    setMsg(null);
    try {
      const qty = Math.min(Math.max(parseInt(genForm.quantity) || 1, 1), 50);
      const res = await api.post('/owner/keys/generate', {
        productId: parseInt(genForm.productId),
        planId: parseInt(genForm.planId),
        quantity: qty,
      });
      setGenResult(res.data);
      toast.success(qty === 1 ? 'Key generated.' : `${qty} keys generated.`);

      // A frissen készült kulcsok LÁTSZANAK is: a szűrők visszaállnak az első
      // lapra, „All" állapotra és „All plans"-re. Enélkül egy másik csomagra
      // (vagy a „Used" fülre) állított lista után úgy tűnne, nem történt semmi.
      setKeysPage(1);
      setKeysFilter('');
      setKeysPlan('');
      setKeysReload(n => n + 1);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error generating key.' });
    } finally {
      setGenLoading(false);
    }
  };

  const globalAction = async (endpoint, label) => {
    setPendingGlobalAction(null);
    setGlobalLoading(endpoint);
    setMsg(null);
    try {
      const res = await api.post(endpoint);
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      const sr = await api.get('/owner/status');
      setSiteStatus(sr.data.status);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || `Error: ${label}` });
    } finally {
      setGlobalLoading(null);
    }
  };

  const toggleStatus = async (newStatus) => {
    setStatusLoading(true);
    setMsg(null);
    try {
      const res = await api.post('/owner/status', { status: newStatus });
      setSiteStatus(res.data.status);
      setMsg({ type: 'success', text: `Site status set to "${res.data.status}".` });
      toast.success(`Site status set to "${res.data.status}".`);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error updating status.' });
    } finally {
      setStatusLoading(false);
    }
  };

  const toggleInviteSystem = async () => {
    setInviteLoading(true);
    setMsg(null);
    const newEnabled = inviteSystem !== 'on';
    try {
      const res = await api.post('/invite/toggle', { enabled: newEnabled });
      setInviteSystem(res.data.inviteSystem);
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error toggling invite system.' });
    } finally {
      setInviteLoading(false);
    }
  };

  const toggleBetaMode = async () => {
    // A BEKAPCSOLÁS KÖVETKEZMÉNYE NEM NYILVÁNVALÓ: minden sima felhasználót
    // kizár a weboldalról ÉS a loaderből is. Ezért kérdezünk rá — a kikapcsolás
    // viszont ártalmatlan, ott nincs kérdés.
    if (betaMode !== 'on' &&
        !window.confirm('Enable closed beta?\n\nEveryone except testers, admins and owners will be blocked from both the website and the loader, and existing sessions end when their token expires. Registration stays open.')) {
      return;
    }
    setBetaLoading(true);
    setMsg(null);
    try {
      const res = await api.post('/owner/beta-mode', { enabled: betaMode !== 'on' });
      setBetaMode(res.data.betaMode);
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error toggling closed beta.' });
    } finally {
      setBetaLoading(false);
    }
  };

  const toggleReferralSystem = async () => {
    setReferralLoading(true);
    setMsg(null);
    try {
      const res = await api.post('/owner/referral-system', { enabled: referralSystem !== 'on' });
      setReferralSystem(res.data.referralSystem);
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error toggling referral system.' });
    } finally {
      setReferralLoading(false);
    }
  };

  const deleteKey = async (key) => {
    setConfirmDeleteKey(null);
    setDeletingKeyId(key.id);
    try {
      await api.delete(`/owner/keys/${key.id}`);
      toast.success('Key deleted.');
      await fetchKeys(keysPage, keysFilter, keysPlan);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error deleting key.');
    } finally {
      setDeletingKeyId(null);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => toast.success('Key copied to clipboard.'));
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success('Key copied to clipboard.');
    }
  };

  if (loading) return <LoadingSpinner fullPage label="Loading owner data…" />;

  return (
    <div className="page-container max-w-6xl">
      <AnnouncementBanner />

      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <span className="eyebrow mb-3">Staff</span>
          <h1 className="font-display text-2xl font-bold text-white tracking-tight">Owner Panel</h1>
          <p className="text-gray-500 text-sm mt-1.5">Full control</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full
                         bg-brand-500/15 text-brand-200 border border-brand-400/25 flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
            <path d="M3 7l4.5 3.5L12 4l4.5 6.5L21 7l-2 12H5L3 7z" />
          </svg>
          Owner
        </span>
      </header>

      <AlertMessage msg={msg} />

      <PanelTabs tabs={TABS} active={ownerTab} onChange={setOwnerTab} className="mb-6" />

      {ownerTab === 'products'      && <ProductsTab />}
      {ownerTab === 'orders'        && <OrdersTab />}
      {ownerTab === 'audit'         && <AuditLogTab />}
      {ownerTab === 'announcements' && <AnnouncementsTab />}
      {ownerTab === 'invites'       && <InvitesTab />}
      {ownerTab === 'traffic'       && <TrafficTab />}
      {ownerTab === 'subscriptions' && <SubscriptionsTab />}
      {ownerTab === 'banlogs'       && <BanLogsTab />}

      {ownerTab === 'dashboard' && (
        <>
          <DashboardStats />

          {/* Site Status + Invite System + Referral System */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 items-start">
            <PanelCard
              title="Site Status"
              subtitle="Shown on the landing page. Global freeze sets this automatically."
            >
              <div className="flex items-center gap-2.5 flex-wrap">
                <SystemPill
                  on={siteStatus === 'working'}
                  onLabel="Working"
                  offLabel="Updating"
                  tone="success"
                />
                <button onClick={() => toggleStatus('working')}
                  disabled={siteStatus === 'working' || statusLoading}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-medium
                             bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300
                             border border-emerald-500/25 transition-all duration-200 ease-smooth
                             disabled:opacity-35 disabled:cursor-not-allowed">
                  Working
                </button>
                <button onClick={() => toggleStatus('updating')}
                  disabled={siteStatus === 'updating' || statusLoading}
                  className="btn-danger py-1.5 px-3.5">
                  Updating
                </button>
              </div>
            </PanelCard>

            <PanelCard
              title="Invite System"
              subtitle="When enabled, registration requires a valid invite code."
            >
              <div className="flex items-center gap-3 flex-wrap">
                <SystemPill on={inviteSystem === 'on'} onLabel="Enabled" offLabel="Disabled" />
                <button onClick={toggleInviteSystem} disabled={inviteLoading}
                  className={inviteSystem === 'on' ? 'btn-danger py-1.5 px-3.5' : 'btn-outline py-1.5 px-3.5 text-sm'}>
                  {inviteLoading ? '…' : inviteSystem === 'on' ? 'Disable' : 'Enable'}
                </button>
              </div>
              {inviteSystem === 'on' && (
                <p className="text-xs text-amber-400/85 mt-3.5 flex items-start gap-1.5">
                  <WarningIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                  New users must provide an invite code to register.
                </p>
              )}
            </PanelCard>

            <PanelCard
              title="Referral System"
              subtitle="When enabled, users can redeem a referral code on their first purchase and the referrer gets +7 days."
            >
              <div className="flex items-center gap-3 flex-wrap">
                <SystemPill on={referralSystem === 'on'} onLabel="Enabled" offLabel="Disabled" />
                <button onClick={toggleReferralSystem} disabled={referralLoading}
                  className={referralSystem === 'on' ? 'btn-danger py-1.5 px-3.5' : 'btn-outline py-1.5 px-3.5 text-sm'}>
                  {referralLoading ? '…' : referralSystem === 'on' ? 'Disable' : 'Enable'}
                </button>
              </div>
              {referralSystem !== 'on' && (
                <p className="text-xs text-amber-400/85 mt-3.5 flex items-start gap-1.5">
                  <WarningIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                  Codes can't be redeemed. Orders placed before this was turned off still pay out.
                </p>
              )}
            </PanelCard>

            <PanelCard
              title="Closed Beta"
              subtitle="When enabled, only testers, admins and owners can sign in — to the website and the loader alike. Registration stays open."
            >
              <div className="flex items-center gap-3 flex-wrap">
                <SystemPill on={betaMode === 'on'} onLabel="Beta only" offLabel="Open" />
                <button onClick={toggleBetaMode} disabled={betaLoading}
                  className={betaMode === 'on' ? 'btn-danger py-1.5 px-3.5' : 'btn-outline py-1.5 px-3.5 text-sm'}>
                  {betaLoading ? '…' : betaMode === 'on' ? 'Disable' : 'Enable'}
                </button>
              </div>
              {betaMode === 'on' && (
                <p className="text-xs text-amber-400/85 mt-3.5 flex items-start gap-1.5">
                  <WarningIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                  Regular users can't sign in anywhere, and existing sessions end when their token expires. You keep access as owner.
                </p>
              )}
            </PanelCard>
          </div>

          {/* Key generation + Global actions */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <PanelCard title="Generate Key" subtitle="Create a single-use license key.">
              <form onSubmit={generateKey} className="space-y-4">
                <div>
                  <label className="label">Product</label>
                  <select className="select" value={genForm.productId}
                    onChange={e => setGenForm(p => ({ ...p, productId: e.target.value, planId: '' }))} required>
                    <option value="">Select a product…</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                {selectedProduct && (
                  <div>
                    <label className="label">Plan</label>
                    <select className="select" value={genForm.planId}
                      onChange={e => setGenForm(p => ({ ...p, planId: e.target.value }))} required>
                      <option value="">Select a plan…</option>
                      {selectedProduct.plans?.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.label} — {durationLabel(plan.days)} (€{plan.price})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="label">Quantity</label>
                  <input
                    type="number" min="1" max="50" className="input"
                    value={genForm.quantity}
                    onChange={e => setGenForm(p => ({ ...p, quantity: e.target.value }))}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary w-full py-2.5" disabled={genLoading}>
                  {genLoading ? 'Generating…' : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                           strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M15 7a5 5 0 11-4.9 6H7v3H4v-3l-1-1 1-1h6.1A5 5 0 0115 7z" />
                      </svg>
                      Generate {parseInt(genForm.quantity, 10) > 1 ? `${genForm.quantity} Keys` : 'Key'}
                    </>
                  )}
                </button>
              </form>

              {genResult && (
                <div className="mt-5 p-4 rounded-xl bg-brand-500/[0.07] border border-brand-400/20">
                  <div className="flex items-center justify-between gap-3 mb-2.5">
                    <p className="text-xs text-brand-200 font-semibold">
                      {genResult.keys.length === 1 ? 'Generated key' : `Generated ${genResult.keys.length} keys`}
                    </p>
                    {genResult.keys.length > 1 && (
                      <button
                        onClick={() => copyToClipboard(genResult.keys.join('\n'))}
                        className="text-xs text-brand-300 hover:text-white transition-colors flex items-center gap-1"
                      >
                        <CopyIcon className="w-3 h-3" /> Copy all
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                    {genResult.keys.map(k => (
                      <div key={k} className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/[0.04]">
                        <code className="text-brand-100 font-mono text-xs tracking-widest flex-1 break-all">{k}</code>
                        <CopyButton value={k} variant="icon" />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2.5">
                    {genResult.label} — {durationLabel(genResult.days)}
                  </p>
                </div>
              )}
            </PanelCard>

            <PanelCard title="Global Actions" subtitle="These actions affect all active subscriptions.">
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-sky-500/[0.07] border border-sky-500/20">
                  <h3 className="text-sm font-semibold text-sky-300 mb-1">Global Freeze</h3>
                  <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                    Pauses all active subscriptions and sets status to "Updating".
                  </p>
                  <button onClick={() => setPendingGlobalAction({ endpoint: '/owner/global-freeze', label: 'Freeze all subscriptions' })}
                    disabled={globalLoading !== null}
                    className="w-full py-2 px-4 rounded-lg text-sm font-medium
                               bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 border border-sky-500/25
                               transition-all duration-200 ease-smooth disabled:opacity-45 disabled:cursor-not-allowed">
                    {globalLoading === '/owner/global-freeze' ? 'Freezing…' : 'Freeze All Subscriptions'}
                  </button>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/20">
                  <h3 className="text-sm font-semibold text-emerald-300 mb-1">Global Unfreeze</h3>
                  <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                    Resumes all frozen subscriptions with time compensated.
                  </p>
                  <button onClick={() => setPendingGlobalAction({ endpoint: '/owner/global-unfreeze', label: 'Unfreeze all subscriptions' })}
                    disabled={globalLoading !== null}
                    className="w-full py-2 px-4 rounded-lg text-sm font-medium
                               bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/25
                               transition-all duration-200 ease-smooth disabled:opacity-45 disabled:cursor-not-allowed">
                    {globalLoading === '/owner/global-unfreeze' ? 'Unfreezing…' : 'Unfreeze All Subscriptions'}
                  </button>
                </div>
              </div>
            </PanelCard>
          </div>

          {/* Key list */}
          <PanelCard
            flush
            title="Generated Keys"
            actions={
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {/* Kategória (csomag) — csak akkor, ha van mit szétválasztani:
                    egyetlen csomagnál a sor puszta zaj lenne. */}
                {keyCategoryFilters.length > 2 && (
                  <FilterTabs
                    filters={keyCategoryFilters}
                    current={keysPlan}
                    onChange={p => { setKeysPlan(p); setKeysPage(1); }}
                  />
                )}
                <FilterTabs
                  filters={KEY_FILTERS}
                  current={keysFilter}
                  onChange={f => { setKeysFilter(f); setKeysPage(1); }}
                />
                <span className="text-xs text-gray-600 tabular-nums">{keysTotal} total</span>
              </div>
            }
          >
            {keys.length === 0 ? (
              <EmptyState
                icon={<path d="M15 7a5 5 0 11-4.9 6H7v3H4v-3l-1-1 1-1h6.1A5 5 0 0115 7z" />}
                title="No keys match this filter."
              />
            ) : (
              <div className="scroll-x">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                    <tr>
                      <th className="text-left px-3 sm:px-5 py-3 table-head">Key</th>
                      <th className="text-left px-3 sm:px-5 py-3 table-head hidden sm:table-cell">Plan</th>
                      <th className="text-left px-3 sm:px-5 py-3 table-head">Status</th>
                      <th className="text-left px-3 sm:px-5 py-3 table-head hidden md:table-cell">Used by</th>
                      <th className="px-3 sm:px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map(k => (
                      <tr key={k.id} className="table-row">
                        <td className="px-3 sm:px-5 py-3 font-mono text-xs text-gray-200 whitespace-nowrap">
                          {k.key_value}
                        </td>
                        <td className="px-3 sm:px-5 py-3 text-gray-400 hidden sm:table-cell">
                          <div>{k.product_name}</div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {k.plan_label}
                            {/* A licenc hosszát a KULCSON befagyott napszám dönti el,
                                nem a csomag mai értéke. Ha a csomagot azóta átírták,
                                ezt ki kell mondani, különben a sor mást ígér, mint
                                amit a kulcs ér. */}
                            {k.days !== k.plan_days && (
                              <span
                                className="text-amber-400/80"
                                title="This key was generated while the plan had a different length. The key's own duration is what the license will run for."
                              >
                                {' '}· {durationLabel(k.days)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-5 py-3">
                          {k.is_used
                            ? <span className="badge-inactive">Activated</span>
                            : k.sold_to
                              ? <span className="badge bg-amber-500/12 text-amber-300 border-amber-500/25">Sold</span>
                              : <span className="badge-active">Available</span>
                          }
                        </td>
                        <td className="px-3 sm:px-5 py-3 text-gray-500 text-xs hidden md:table-cell">
                          {k.is_used
                            ? (k.used_by_name || '—')
                            : k.sold_to
                              ? <span className="text-amber-400">{k.sold_to_name || '—'}</span>
                              : '—'
                          }
                        </td>
                        <td className="px-3 sm:px-5 py-3">
                          {!k.is_used && (
                            <div className="flex items-center gap-1 justify-end">
                              <CopyButton value={k.key_value} variant="icon" title="Copy key" />
                              <button onClick={() => setConfirmDeleteKey(k)} disabled={deletingKeyId === k.id}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                           text-gray-500 hover:text-red-300 hover:bg-red-500/10
                                           transition-colors disabled:opacity-40"
                                title="Delete key">
                                {deletingKeyId === k.id
                                  ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                                  : <TrashIcon className="w-3.5 h-3.5" />
                                }
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PanelCard>

          <Pagination
            page={keysPage}
            totalPages={Math.ceil(keysTotal / KEYS_PAGE_SIZE)}
            total={keysTotal}
            label="keys"
            onPage={setKeysPage}
            className="mt-4"
          />
        </>
      )}

      {pendingGlobalAction && (
        <ConfirmModal
          title="Confirm global action"
          message={`Are you sure you want to: ${pendingGlobalAction.label}?`}
          confirmLabel="Confirm"
          danger
          onConfirm={() => globalAction(pendingGlobalAction.endpoint, pendingGlobalAction.label)}
          onCancel={() => setPendingGlobalAction(null)}
        />
      )}

      {confirmDeleteKey && (
        <ConfirmModal
          title="Delete key?"
          message={`"${confirmDeleteKey.key_value}" — this cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => deleteKey(confirmDeleteKey)}
          onCancel={() => setConfirmDeleteKey(null)}
        />
      )}
    </div>
  );
}
